/**
 * Global Search Modal
 * SCR-003: Cross-module search functionality
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-003 Global Search
 * @iso27001 A.9.4.1 Information access restriction
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Spinner, Badge } from '@myvault/shared/components';
import { useSearch } from './index';
import { SearchHistoryPanel } from './components/SearchHistoryPanel';
import { SavedSearchesPanel } from './components/SavedSearchesPanel';
import { useCreateSavedSearch } from './hooks/useSearchPersistence';

// ============= Types =============

type SearchResultType = 'document' | 'category' | 'delegate' | 'setting';
type FilterTab = 'all' | 'documents' | 'categories' | 'delegates';

interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  category?: string;
  highlight?: string;
  url: string;
  icon: 'document' | 'folder' | 'user' | 'cog';
}

interface SearchSuggestion {
  query: string;
  type: 'recent' | 'popular';
}

interface SearchResponse {
  items: SearchResult[];
  total: number;
  counts: Record<string, number>;
}

// ============= API =============

const API_BASE = '/api/v1';
const TOKEN_KEY = 'myvault_access_token';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function fetchSearchResults(query: string, filter: FilterTab): Promise<SearchResponse> {
  const params = new URLSearchParams({
    q: query,
    ...(filter !== 'all' && { filter: filter }),
    limit: '10',
  });

  const response = await fetch(`${API_BASE}/search?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Search failed');
  }

  const data = await response.json();

  // Map backend response to frontend SearchResult shape
  const items: SearchResult[] = (data.items || []).map((item: Record<string, unknown>) => ({
    id: item.id as string,
    type: item.type as SearchResultType,
    title: item.title as string,
    subtitle: item.subtitle as string | undefined,
    category: item.category as string | undefined,
    highlight: undefined, // Not provided by backend
    url: (item.href as string) || `/documents/${item.id}`,
    icon: mapIconName(item.icon as string),
  }));

  return {
    items,
    total: data.total || 0,
    counts: data.counts || {},
  };
}

// Map backend icon names to frontend icon names
function mapIconName(icon: string): SearchResult['icon'] {
  switch (icon) {
    case 'photo':
    case 'documentText':
    case 'document':
    case 'tableCells':
      return 'document';
    case 'folder':
      return 'folder';
    case 'user':
      return 'user';
    case 'cog':
      return 'cog';
    default:
      return 'document';
  }
}

async function fetchSearchSuggestions(query: string): Promise<SearchSuggestion[]> {
  const response = await fetch(`${API_BASE}/search/suggestions?q=${encodeURIComponent(query)}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  // Backend returns { suggestions: string[] }, map to SearchSuggestion[]
  return (data.suggestions || []).map((term: string) => ({
    query: term,
    type: 'popular' as const,
  }));
}


// ============= Components =============

function SearchInput({
  value,
  onChange,
  onClose,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-mv-gray-200 px-4 py-3">
      <Icon name="magnifyingGlass" variant="outline" size="md" className="text-mv-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Zoek documenten, categorien, gemachtigden..."
        className="flex-1 bg-transparent text-lg text-mv-gray-900 placeholder-mv-gray-400 outline-none"
        autoComplete="off"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="rounded p-1 hover:bg-mv-gray-100"
          aria-label="Wis zoekopdracht"
        >
          <Icon name="xMark" variant="outline" size="sm" className="text-mv-gray-400" />
        </button>
      )}
      <kbd className="hidden rounded border border-mv-gray-300 bg-mv-gray-100 px-2 py-0.5 text-xs text-mv-gray-500 sm:inline">
        ESC
      </kbd>
    </div>
  );
}

function FilterTabs({
  activeFilter,
  onFilterChange,
  resultCounts,
}: {
  activeFilter: FilterTab;
  onFilterChange: (filter: FilterTab) => void;
  resultCounts: Record<FilterTab, number>;
}) {
  const tabs: { id: FilterTab; label: string }[] = [
    { id: 'all', label: 'Alles' },
    { id: 'documents', label: 'Documenten' },
    { id: 'categories', label: 'Categorieën' },
    { id: 'delegates', label: 'Gemachtigden' },
  ];

  return (
    <div className="flex gap-2 border-b border-mv-gray-200 px-4 py-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onFilterChange(tab.id)}
          className={`flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            activeFilter === tab.id
              ? 'bg-mv-blue-100 text-mv-blue-700'
              : 'text-mv-gray-600 hover:bg-mv-gray-100'
          }`}
        >
          {tab.label}
          {resultCounts[tab.id] > 0 && (
            <span className="rounded-full bg-mv-gray-200 px-1.5 text-xs">
              {resultCounts[tab.id]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function SuggestionsList({
  suggestions,
  onSelect,
}: {
  suggestions: SearchSuggestion[];
  onSelect: (query: string) => void;
}) {
  if (suggestions.length === 0) return null;

  return (
    <div className="px-4 py-3">
      <span className="mb-2 block text-sm font-medium text-mv-gray-500">Suggesties</span>
      <ul className="space-y-1">
        {suggestions.map((suggestion, index) => (
          <li key={index}>
            <button
              onClick={() => onSelect(suggestion.query)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-mv-gray-50"
            >
              <Icon
                name={suggestion.type === 'recent' ? 'clock' : 'sparkles'}
                variant="outline"
                size="sm"
                className="text-mv-gray-400"
              />
              <span className="text-mv-gray-700">{suggestion.query}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchResultItem({
  result,
  onClick,
  isActive,
}: {
  result: SearchResult;
  onClick: () => void;
  isActive: boolean;
}) {
  const iconMap: Record<SearchResult['icon'], 'document' | 'folder' | 'user' | 'cog'> = {
    document: 'document',
    folder: 'folder',
    user: 'user',
    cog: 'cog',
  };

  const typeLabels: Record<SearchResultType, string> = {
    document: 'Document',
    category: 'Categorie',
    delegate: 'Gemachtigde',
    setting: 'Instelling',
  };

  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-4 px-4 py-3 text-left transition-colors ${
        isActive ? 'bg-mv-blue-50' : 'hover:bg-mv-gray-50'
      }`}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
          isActive ? 'bg-mv-blue-100' : 'bg-mv-gray-100'
        }`}
      >
        <Icon
          name={iconMap[result.icon]}
          variant="outline"
          size="sm"
          className={isActive ? 'text-mv-blue-600' : 'text-mv-gray-600'}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-mv-gray-900">{result.title}</span>
          <Badge variant="default" className="text-xs">
            {typeLabels[result.type]}
          </Badge>
        </div>
        {result.subtitle && (
          <p className="truncate text-sm text-mv-gray-500">{result.subtitle}</p>
        )}
        {result.highlight && (
          <p
            className="mt-1 truncate text-sm text-mv-gray-600"
            dangerouslySetInnerHTML={{ __html: result.highlight }}
          />
        )}
      </div>
      <Icon name="chevronRight" variant="outline" size="sm" className="text-mv-gray-400" />
    </button>
  );
}

function SearchResultsList({
  results,
  onSelect,
  activeIndex,
  onActiveIndexChange,
}: {
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon
          name="magnifyingGlass"
          variant="outline"
          size="lg"
          className="mb-3 text-mv-gray-300"
        />
        <p className="font-medium text-mv-gray-600">Geen resultaten gevonden</p>
        <p className="mt-1 text-sm text-mv-gray-400">
          Probeer een andere zoekopdracht of filter
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      <ul>
        {results.map((result, index) => (
          <li
            key={result.id}
            onMouseEnter={() => onActiveIndexChange(index)}
          >
            <SearchResultItem
              result={result}
              onClick={() => onSelect(result)}
              isActive={index === activeIndex}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Spinner size="md" />
      <span className="ml-3 text-mv-gray-500">Zoeken...</span>
    </div>
  );
}

function SaveSearchInline({
  query,
  onSaved,
}: {
  query: string;
  onSaved?: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateSavedSearch();

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Naam is verplicht');
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        query,
      });
      setName('');
      setIsExpanded(false);
      setError(null);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-mv-blue-600 hover:bg-mv-blue-50 hover:text-mv-blue-700"
      >
        <Icon name="bookmark" variant="outline" size="xs" />
        Bewaar zoekopdracht
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        placeholder="Naam..."
        className={`w-32 rounded border px-2 py-1 text-xs focus:outline-none focus:ring-1 ${
          error
            ? 'border-mv-red-300 focus:border-mv-red-500 focus:ring-mv-red-500'
            : 'border-mv-gray-300 focus:border-mv-blue-500 focus:ring-mv-blue-500'
        }`}
        maxLength={100}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
          } else if (e.key === 'Escape') {
            setIsExpanded(false);
            setName('');
            setError(null);
          }
        }}
      />
      <button
        onClick={handleSave}
        disabled={createMutation.isPending}
        className="rounded bg-mv-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-mv-blue-700 disabled:opacity-50"
      >
        {createMutation.isPending ? '...' : 'Opslaan'}
      </button>
      <button
        onClick={() => {
          setIsExpanded(false);
          setName('');
          setError(null);
        }}
        className="rounded px-2 py-1 text-xs text-mv-gray-500 hover:bg-mv-gray-100"
      >
        Annuleer
      </button>
    </div>
  );
}

function KeyboardHints({ showSaveButton, query }: { showSaveButton?: boolean; query?: string }) {
  return (
    <div className="flex items-center justify-between border-t border-mv-gray-200 px-4 py-2">
      <div className="flex gap-4 text-xs text-mv-gray-400">
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-mv-gray-300 bg-mv-gray-100 px-1.5 py-0.5">↑</kbd>
          <kbd className="rounded border border-mv-gray-300 bg-mv-gray-100 px-1.5 py-0.5">↓</kbd>
          navigeren
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-mv-gray-300 bg-mv-gray-100 px-1.5 py-0.5">↵</kbd>
          openen
        </span>
      </div>
      {showSaveButton && query ? (
        <SaveSearchInline query={query} />
      ) : (
        <span className="text-xs text-mv-gray-400">
          Druk op <kbd className="rounded border border-mv-gray-300 bg-mv-gray-100 px-1.5 py-0.5">/</kbd> om te zoeken
        </span>
      )}
    </div>
  );
}

// ============= Main Component =============

export function GlobalSearchModal() {
  const navigate = useNavigate();
  const { isSearchOpen, closeSearch, query, setQuery } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [activeIndex, setActiveIndex] = useState(0);

  // Debounced query
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Search query
  const {
    data: searchData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['search', debouncedQuery, activeFilter],
    queryFn: () => fetchSearchResults(debouncedQuery, activeFilter),
    enabled: isSearchOpen && debouncedQuery.length >= 2,
    staleTime: 1000 * 60, // 1 minute
  });

  const results = searchData?.items || [];
  const resultCounts: Record<FilterTab, number> = {
    all: searchData?.total || 0,
    documents: searchData?.counts?.documents || results.filter((r) => r.type === 'document').length,
    categories: searchData?.counts?.folders || results.filter((r) => r.type === 'category').length,
    delegates: results.filter((r) => r.type === 'delegate').length,
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isSearchOpen) return;

      switch (e.key) {
        case 'Escape':
          closeSearch();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[activeIndex]) {
            handleSelectResult(results[activeIndex]);
          }
          break;
      }
    },
    [isSearchOpen, results, activeIndex, closeSearch]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        closeSearch();
      }
    };

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen, closeSearch]);

  const handleSelectResult = (result: SearchResult) => {
    // Note: Search history is now automatically tracked via API (SCR-106)
    closeSearch();
    navigate(result.url);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setQuery(suggestion);
  };

  // Handler for saved searches (may include filters)
  const handleSelectSavedSearch = (searchQuery: string, filters?: Record<string, unknown>) => {
    setQuery(searchQuery);
    // TODO: Apply filters when filter system is enhanced
  };

  if (!isSearchOpen) return null;

  const showInitialState = query.length < 2;
  const showLoading = (isLoading || isFetching) && query.length >= 2;
  const showResults = !showLoading && results.length > 0;
  const showNoResults = !showLoading && results.length === 0 && query.length >= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[10vh]">
      <div
        ref={modalRef}
        className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Zoeken"
      >
        <SearchInput
          value={query}
          onChange={setQuery}
          onClose={closeSearch}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
        />

        {!showInitialState && (
          <FilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            resultCounts={resultCounts}
          />
        )}

        <div className="min-h-[200px]">
          {showInitialState && (
            <>
              {/* API-backed search history (SCR-106) */}
              <SearchHistoryPanel onSelectSearch={handleSelectSuggestion} limit={5} />

              {/* API-backed saved searches (SCR-106) */}
              <SavedSearchesPanel
                onSelectSearch={handleSelectSavedSearch}
                currentQuery={query}
              />
            </>
          )}

          {showLoading && <LoadingState />}

          {showResults && (
            <SearchResultsList
              results={results}
              onSelect={handleSelectResult}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
            />
          )}

          {showNoResults && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon
                name="magnifyingGlass"
                variant="outline"
                size="lg"
                className="mb-3 text-mv-gray-300"
              />
              <p className="font-medium text-mv-gray-600">
                Geen resultaten voor "{query}"
              </p>
              <p className="mt-1 text-sm text-mv-gray-400">
                Probeer andere zoektermen
              </p>
            </div>
          )}
        </div>

        <KeyboardHints showSaveButton={showResults || showNoResults} query={query} />
      </div>
    </div>
  );
}

export default GlobalSearchModal;
